import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import AppAddPillButton from '../../../../components/designe/buttons/app-add-pill-button/AppAddPillButton';
import { useRegisterPageContainerError } from '../../../../components/layout/page-container/page-container-error-handler';
import { useRegisterPageRefreshHandler } from '../../../../components/layout/page-container/page-refresh-handler';
import CommunitiesListBig from '../../../../components/reusable/communities-list/CommunitiesListBig';
import { CommunitiesListRefreshProvider } from '../../../../components/reusable/communities-list/CommunitiesListRefreshContext';
import LoadMoreFeedback from '../../../../components/reusable/load-more-feedback/LoadMoreFeedback';
import Search from '../../../../components/reusable/search/Search';
import { useNavigationStack } from '../../../../context/NavigationStackContext';
import { useLazyLoad } from '../../../../hooks/use-lazy-load';
import { useDebounce } from '../../../../hooks/useDebounce';
import { useLazyGetCommunities } from '../../../../redux/api/communitiesApi';
import { Paths } from '../../../../shared/paths';
import { useStyles } from './AllCommunities.styles';

export default function AllCommunities() {
  const [filter, setFilter] = useState('');
  const {
    items: communities,
    footerRef,
    resetAll,
    refetchAll,
    retryLastRequest,
    isFetching: isFetchingCommunities,
    error: communitiesError,
  } = useLazyLoad(useLazyGetCommunities, { searchValue: filter });
  const theme = useTheme();

  const { classes } = useStyles(theme);
  const { navigate } = useNavigationStack();

  const resetDebounced = useCallback(() => {
    void resetAll(true);
  }, [resetAll]);
  const [debouncedSearch] = useDebounce(resetDebounced, 500);
  useRegisterPageRefreshHandler(() => resetAll(true));

  const firstFetchStartedRef = useRef(false);
  const [isFirstFetchSettled, setIsFirstFetchSettled] = useState(false);
  const [isSearchRefreshPending, setIsSearchRefreshPending] = useState(false);

  useEffect(() => {
    if (isFetchingCommunities) {
      firstFetchStartedRef.current = true;
      return;
    }
    if (isFirstFetchSettled || !firstFetchStartedRef.current) {
      return;
    }
    setIsFirstFetchSettled(true);
  }, [isFetchingCommunities, isFirstFetchSettled]);

  useEffect(() => {
    if (
      isSearchRefreshPending &&
      !isFetchingCommunities &&
      communities !== null
    ) {
      setIsSearchRefreshPending(false);
    }
  }, [isSearchRefreshPending, isFetchingCommunities, communities]);

  const isFilterReloadingInProgress =
    isSearchRefreshPending &&
    (communities === null || communities?.length === 0);
  const isLoading = Boolean(
    !isFirstFetchSettled ||
      isFilterReloadingInProgress ||
      (isFetchingCommunities &&
        (communities === null || communities.length === 0)),
  );
  const pageDataOwners = useMemo(
    () => ({
      communities: communities ?? null,
    }),
    [communities],
  );
  const handleRetry = useCallback(() => {
    void retryLastRequest();
  }, [retryLastRequest]);
  useRegisterPageContainerError({
    errors: communitiesError ? [communitiesError] : [],
    isLoading,
    hasData: Boolean(pageDataOwners.communities?.length),
    onRetry: handleRetry,
  });

  const prevFilterRef = useRef<string | null>(null);
  useEffect(() => {
    if (prevFilterRef.current === null) {
      prevFilterRef.current = filter;
      return;
    }
    if (prevFilterRef.current === filter) {
      return;
    }
    prevFilterRef.current = filter;
    setIsSearchRefreshPending(true);
    debouncedSearch();
  }, [filter, debouncedSearch]);

  function addNewExerciseClicked() {
    navigate(Paths.communities.toNew);
  }

  return (
    <Box>
      <Box
        display="flex"
        alignItems="flex-end"
        justifyContent="flex-end"
        gap={1}>
        <Search
          fullWidth
          value={filter}
          onChange={setFilter}
          className={classes.allCommunitiesSearch}
        />
        <AppAddPillButton onClick={() => addNewExerciseClicked()} />
      </Box>

      <CommunitiesListRefreshProvider refetch={refetchAll}>
        <CommunitiesListBig
          communities={communities}
          showAddButton={true}
          isLoading={
            (!communities || communities.length === 0) &&
            Boolean(isFetchingCommunities)
          }
          isError={Boolean(communitiesError) && communities == null}
          listFooter={
            <>
              <LoadMoreFeedback
                error={communitiesError}
                isFetching={
                  Boolean(isFetchingCommunities) && Boolean(communities?.length)
                }
                onRetry={refetchAll}
              />
              <Box ref={footerRef} mb={1} />
            </>
          }
        />
      </CommunitiesListRefreshProvider>
    </Box>
  );
}
